import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth, formatApiError } from "../lib/auth";
import { Loader2, LogIn } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
    const { login } = useAuth();
    const nav = useNavigate();
    const loc = useLocation();
    const from = loc.state?.from || "/dashboard";
    const [email, setEmail] = useState("demo@audri.app");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        setErr("");
        try {
            await login(email.trim(), password);
            toast.success("Welcome back");
            nav(from, { replace: true });
        } catch (e2) {
            setErr(formatApiError(e2, "Login failed"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <AuthShell
            eyebrow="Restricted access"
            title="Welcome back to audri."
            sub="Staff sign-in only. Public users — please use the live demo from the homepage."
        >
            <form onSubmit={submit} className="space-y-4">
                <Field
                    label="Work email"
                    value={email}
                    onChange={(v) => setEmail(v)}
                    type="email"
                    autoFocus
                    testid="login-email"
                />
                <Field
                    label="Password"
                    value={password}
                    onChange={(v) => setPassword(v)}
                    type="password"
                    testid="login-password"
                />
                {err && (
                    <div
                        className="text-[12px] text-[#991B1B] border border-[#FECACA] bg-[#FEF2F2] px-3 py-2 rounded-sm"
                        data-testid="login-error"
                    >
                        {err}
                    </div>
                )}
                <button
                    type="submit"
                    disabled={busy}
                    data-testid="login-submit"
                    className="w-full inline-flex items-center justify-center gap-2 bg-[#C2410C] text-white text-sm px-4 py-3 rounded-sm hover:bg-[#9A3412] disabled:opacity-60"
                >
                    {busy ? (
                        <Loader2 size={14} className="animate-spin" />
                    ) : (
                        <LogIn size={14} />
                    )}
                    Sign in
                </button>
                <div className="flex items-center justify-end text-[12px] pt-2">
                    <Link
                        to="/"
                        className="link-underline text-neutral-500"
                        data-testid="back-home-link"
                    >
                        ← Back to homepage
                    </Link>
                </div>
            </form>
        </AuthShell>
    );
}

export function AuthShell({ eyebrow, title, sub, children }) {
    return (
        <div className="min-h-screen bg-[#fafafa] flex">
            {/* Left panel — brand story */}
            <div className="hidden md:flex md:w-1/2 bg-[#0a0a0a] text-white flex-col justify-between p-12 relative overflow-hidden">
                <div className="flex items-center gap-2">
                    <img
                        src="/audri-logo.png"
                        alt="AudRI"
                        className="w-10 h-10 rounded-sm"
                    />
                    <div>
                        <div className="heading font-bold tracking-tight text-xl leading-none">
                            audri
                        </div>
                        <div className="overline text-neutral-400 mt-1">
                            Audit Realtime Intelligence
                        </div>
                    </div>
                </div>
                <div>
                    <div className="overline text-[#C2410C] mb-6">
                        AudRI for GxP · For QA leaders in lifesciences
                    </div>
                    <h2 className="heading text-4xl lg:text-5xl leading-tight tracking-tighter font-medium">
                        GxP audits,
                        <br />
                        <span className="text-[#C2410C]">reduced 85–90%.</span>
                    </h2>
                    <p className="mt-6 text-neutral-400 leading-relaxed max-w-md">
                        AudRI automates periodic reviews for Computer System
                        Validation — ingest your SOPs and validation packages,
                        extract rules, match evidence, generate inspection-ready
                        findings.
                    </p>
                </div>
                <div className="text-[11px] mono tracking-widest uppercase text-neutral-500">
                    21 CFR Part 11 · GAMP 5 · Annex 11 · ALCOA+
                </div>
            </div>

            {/* Right panel — form */}
            <div className="flex-1 flex items-center justify-center p-8 md:p-16">
                <div className="w-full max-w-md">
                    <Link
                        to="/"
                        className="md:hidden flex items-center gap-2 mb-10"
                    >
                        <img
                            src="/audri-logo.png"
                            alt="AudRI"
                            className="w-8 h-8 rounded-sm"
                        />
                        <span className="heading font-bold">audri</span>
                    </Link>
                    {eyebrow && <div className="overline mb-4">{eyebrow}</div>}
                    <h1 className="heading text-3xl md:text-4xl font-medium tracking-tighter">
                        {title}
                    </h1>
                    {sub && (
                        <p className="text-sm text-neutral-600 mt-3 leading-relaxed">
                            {sub}
                        </p>
                    )}
                    <div className="mt-10">{children}</div>
                </div>
            </div>
        </div>
    );
}

export function Field({ label, value, onChange, type = "text", autoFocus, testid, placeholder }) {
    return (
        <label className="block">
            <span className="overline">{label}</span>
            <input
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                autoFocus={autoFocus}
                placeholder={placeholder}
                data-testid={testid}
                className="mt-1 block w-full border border-hair bg-white px-3 py-2 text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-[#C2410C] focus:border-[#C2410C]"
            />
        </label>
    );
}
